/**
 * TDBLeaderCockpit — the 6-bucket leader view of TDB monthly/weekly P&L.
 *
 * Surfaces:
 * - Period selector (newest period auto-loaded)
 * - Loud red alert banners (Gurgaon losing, Manpower too high, etc.)
 * - Group totals card (Income · Expense · GOP)
 * - Per-bucket grid (Food / Marketing / Manpower / Utilities / Operating / Admin)
 *   with click-to-expand sub-categories
 * - Per-city breakdown table
 * - "Upload new MIS xlsx" button (top-right)
 *
 * Aditya (TDB leader), Nihal (accountant), Roji (co-leader), and Dipali
 * (CEO) all see this. Access is gated by `_has_tdb_access()` on the
 * backend.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Upload, AlertCircle, ChevronDown, ChevronRight, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtINR(v) {
  if (v == null) return '—';
  if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
  if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
  if (Math.abs(v) >= 1000) return `₹${(v / 1000).toFixed(1)}K`;
  return `₹${v.toFixed(0)}`;
}

function fmtPct(v) {
  if (v == null) return '—';
  return `${v.toFixed(1)}%`;
}

function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function TDBLeaderCockpit() {
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [expandedBucket, setExpandedBucket] = useState(null);

  const token = localStorage.getItem('token');

  const loadPeriods = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/tdb/mis/periods`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPeriods(r.data.periods || []);
      if (r.data.periods?.length && !selectedPeriod) {
        setSelectedPeriod(r.data.periods[0]);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  }, [token, selectedPeriod]);

  const loadCockpit = useCallback(async (period) => {
    if (!period) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/tdb/mis/leader-cockpit?period_start=${period.period_start}&period_end=${period.period_end}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadPeriods(); }, [loadPeriods]);
  useEffect(() => { if (selectedPeriod) loadCockpit(selectedPeriod); }, [selectedPeriod, loadCockpit]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    const form = new FormData();
    form.append('file', file);
    form.append('period_type', 'monthly');
    try {
      const r = await axios.post(`${API}/tdb/mis/upload`, form, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      await loadPeriods();
      setSelectedPeriod({ period_start: r.data.period_start, period_end: r.data.period_end });
    } catch (e2) {
      setError(e2?.response?.data?.detail || e2.message);
    } finally {
      setUploading(false);
      e.target.value = null;
    }
  };

  if (loading && !data) {
    return <div className="p-8 text-center text-sm text-[#6B7280]" data-testid="cockpit-loading">Loading…</div>;
  }

  return (
    <div className="space-y-4" data-testid="tdb-leader-cockpit">
      {/* ── Header row · period selector + upload ── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-[#1B2A4A]">Leader Cockpit · MIS Truth</h2>
          {periods.length > 0 && (
            <select
              value={selectedPeriod ? `${selectedPeriod.period_start}|${selectedPeriod.period_end}` : ''}
              onChange={(e) => {
                const [ps, pe] = e.target.value.split('|');
                setSelectedPeriod({ period_start: ps, period_end: pe });
              }}
              data-testid="cockpit-period-selector"
              className="px-3 py-1.5 rounded-md text-sm border border-[#185FA5] bg-white text-[#1B2A4A] cursor-pointer">
              {periods.map(p => (
                <option key={`${p.period_start}|${p.period_end}`} value={`${p.period_start}|${p.period_end}`}>
                  {fmtDate(p.period_start)} → {fmtDate(p.period_end)} · {p.period_type}
                </option>
              ))}
            </select>
          )}
        </div>
        <label className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-white cursor-pointer transition-all hover:opacity-90"
          style={{ backgroundColor: '#185FA5' }}
          data-testid="cockpit-upload-btn">
          <Upload size={14} />
          {uploading ? 'Uploading…' : 'Upload MIS xlsx'}
          <input type="file" accept=".xlsx,.xls" onChange={handleUpload} className="hidden" disabled={uploading} />
        </label>
      </div>

      {error && (
        <div className="p-3 rounded-md text-sm" style={{ backgroundColor: '#FCEBEB', color: '#A32D2D', border: '1px solid #E8B4B4' }} data-testid="cockpit-error">
          {error}
        </div>
      )}

      {!data?.ok && !error && (
        <div className="p-8 text-center text-sm text-[#6B7280] border border-dashed border-[#CBD5E1] rounded-md" data-testid="cockpit-empty">
          <FileText size={28} className="mx-auto mb-2 text-[#94A3B8]" />
          No MIS data uploaded yet. Click <strong>Upload MIS xlsx</strong> above to ingest Mickky's monthly P&L file.
        </div>
      )}

      {data?.ok && (
        <>
          {/* ── Loud red alert banner ── */}
          {data.alerts?.length > 0 && (
            <div className="space-y-2" data-testid="cockpit-alerts">
              {data.alerts.filter(a => a.severity === 'red').map((a, i) => (
                <div key={i}
                  className="flex items-start gap-3 px-4 py-3 rounded-md"
                  style={{ backgroundColor: '#FCEBEB', border: '1.5px solid #A32D2D' }}
                  data-testid={`cockpit-alert-red-${i}`}>
                  <AlertCircle size={18} className="flex-shrink-0 mt-0.5" style={{ color: '#A32D2D' }} />
                  <div className="text-sm font-semibold" style={{ color: '#A32D2D' }}>
                    🚨 {a.message}
                  </div>
                </div>
              ))}
              {data.alerts.filter(a => a.severity === 'amber').map((a, i) => (
                <div key={`amber-${i}`}
                  className="flex items-start gap-2 px-3 py-2 rounded-md text-xs"
                  style={{ backgroundColor: '#FEF6E4', border: '1px solid #D97706', color: '#92400E' }}
                  data-testid={`cockpit-alert-amber-${i}`}>
                  <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                  <div>⚠ {a.message}</div>
                </div>
              ))}
            </div>
          )}

          {/* ── Group totals card ── */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="cockpit-group-totals">
            <KpiCard label="Group Income" value={fmtINR(data.group_totals.income)} accent="#185FA5" />
            <KpiCard label="Group Expense" value={fmtINR(data.group_totals.expense)} accent="#6B7280" />
            <KpiCard
              label="Group GOP"
              value={fmtINR(data.group_totals.gop)}
              accent={data.group_totals.gop >= 0 ? '#3B6D11' : '#A32D2D'}
              testId="group-gop"
            />
            <KpiCard
              label="GOP %"
              value={fmtPct(data.group_totals.gop_pct)}
              accent={data.group_totals.gop_pct >= 5 ? '#3B6D11' : data.group_totals.gop_pct >= 0 ? '#D97706' : '#A32D2D'}
              testId="group-gop-pct"
            />
          </div>

          {/* ── 6-bucket grid · click to expand sub-categories ── */}
          <div className="rounded-md border border-[#E5E1DC] bg-white" data-testid="cockpit-buckets">
            <div className="px-4 py-2 border-b border-[#E5E1DC] bg-[#F7F5F2]">
              <p className="text-[11px] tracking-widest uppercase text-[#6B7280]">Cost breakdown · 6 buckets</p>
            </div>
            {Object.entries(data.group_totals.buckets).map(([bk, bv]) => {
              const tone = pctTone(bk, bv.pct_of_income);
              const isExpanded = expandedBucket === bk;
              return (
                <div key={bk}>
                  <div
                    role="button"
                    onClick={() => setExpandedBucket(isExpanded ? null : bk)}
                    className="flex items-center justify-between px-4 py-3 border-b border-[#F0EBE5] cursor-pointer hover:bg-[#FAFAF7] transition-colors"
                    data-testid={`bucket-row-${bk}`}>
                    <div className="flex items-center gap-2">
                      {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      <span className="text-lg">{bv.icon}</span>
                      <span className="text-sm font-medium text-[#1B2A4A]">{bv.label}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm tabular-nums text-[#1B2A4A]">{fmtINR(bv.total)}</span>
                      <span className="text-xs tabular-nums px-2 py-0.5 rounded-full font-medium"
                        style={{ backgroundColor: tone.bg, color: tone.fg }}>
                        {fmtPct(bv.pct_of_income)}
                      </span>
                    </div>
                  </div>
                  {isExpanded && bv.lines?.length > 0 && (
                    <div className="bg-[#FAFAF7] border-b border-[#F0EBE5] px-4 py-2">
                      {bv.lines.map((line, i) => (
                        <div key={i} className="flex items-center justify-between py-1 text-xs text-[#6B7280]">
                          <span className="pl-6">↳ {line.label}</span>
                          <span className="tabular-nums">{fmtINR(line.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── Per-city table ── */}
          <div className="rounded-md border border-[#E5E1DC] bg-white overflow-x-auto" data-testid="cockpit-city-table">
            <div className="px-4 py-2 border-b border-[#E5E1DC] bg-[#F7F5F2]">
              <p className="text-[11px] tracking-widest uppercase text-[#6B7280]">Per-city breakdown</p>
            </div>
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wider text-[#6B7280] bg-[#FAFAF7]">
                <tr>
                  <th className="px-4 py-2 text-left">City</th>
                  <th className="px-4 py-2 text-right">Income</th>
                  <th className="px-4 py-2 text-right">Expense</th>
                  <th className="px-4 py-2 text-right">GOP</th>
                  <th className="px-4 py-2 text-right">GOP %</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(data.by_city).map(([city, c]) => (
                  <tr key={city} className="border-t border-[#F0EBE5]" data-testid={`city-row-${city.toLowerCase()}`}>
                    <td className="px-4 py-2.5 font-medium text-[#1B2A4A]">{city}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{fmtINR(c.grand_total_income)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{fmtINR(c.grand_total_expense)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-semibold"
                      style={{ color: c.gop >= 0 ? '#3B6D11' : '#A32D2D' }}>
                      {fmtINR(c.gop)}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums"
                      style={{ color: c.gop_pct >= 5 ? '#3B6D11' : c.gop_pct >= 0 ? '#D97706' : '#A32D2D' }}>
                      {fmtPct(c.gop_pct)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Footer · provenance ── */}
          <div className="text-xs text-[#9CA3AF] text-right">
            Source: <span className="font-medium">{data.source_file_name || 'MIS upload'}</span>
            {data.uploaded_by && <> · uploaded by {data.uploaded_by}</>}
            {data.uploaded_at && <> · {fmtDate(data.uploaded_at)}</>}
          </div>
        </>
      )}
    </div>
  );
}

function KpiCard({ label, value, accent = '#185FA5', testId }) {
  return (
    <div className="rounded-md border border-[#E5E1DC] bg-white p-3" data-testid={testId || `kpi-${label.replace(/\s/g,'-').toLowerCase()}`}>
      <p className="text-[10px] tracking-widest uppercase text-[#6B7280]">{label}</p>
      <p className="text-lg font-bold tabular-nums mt-0.5" style={{ color: accent }}>{value}</p>
    </div>
  );
}

// Tone-of-voice colour mapping by bucket key + pct
function pctTone(bucketKey, pct) {
  // Marketing red below 0.5% (no spend); others red above their healthy max
  const thresholds = {
    food_cost:    { healthyMax: 8, redAt: 12 },
    manpower:     { healthyMax: 35, redAt: 45 },
    operating:    { healthyMax: 35, redAt: 45 },
    marketing:    { healthyMin: 0.5, healthyMax: 99 },
  };
  const t = thresholds[bucketKey];
  if (!t) return { bg: '#F1F5F9', fg: '#475569' };
  if (t.healthyMin != null && pct < t.healthyMin) return { bg: '#FCEBEB', fg: '#A32D2D' };
  if (t.redAt != null && pct > t.redAt) return { bg: '#FCEBEB', fg: '#A32D2D' };
  if (t.healthyMax != null && pct > t.healthyMax) return { bg: '#FEF6E4', fg: '#92400E' };
  return { bg: '#E8F7F4', fg: '#0F766E' };
}
